import api from './client';
import { BillingCycle, SubscriptionPlan } from './subscriptionPlans';

export type SubscriptionStatus =
  | 'active'
  | 'cancelled'
  | 'expired'
  | 'past_due'
  | 'incomplete'
  | 'trialing';

export interface UserSubscription {
  id: number;
  userId: number;
  planId: number;
  planName: string;
  billingCycle: BillingCycle;
  /** Sequelize DECIMAL ships as a string over JSON. */
  amount: string | number;
  currency: string;
  status: SubscriptionStatus;
  startDate: string;
  /** Paid-through date (for a trial: trial end + first billing cycle). */
  expiryDate: string;
  /** End of the free trial = first charge date. Null when there was no trial. */
  trialEndsAt?: string | null;
  cancelledAt?: string | null;
  autoRenew: boolean;
  paymentMethod?: string | null;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  stripePaymentIntentId?: string | null;
  stripeInvoiceId?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt?: string;
  updatedAt?: string;
  plan?: SubscriptionPlan;
}

export interface SubscribePayload {
  planId: number;
  /** A Stripe card token (tok_…) or PaymentMethod id (pm_…). */
  stripeToken: string;
  paymentMethod?: string;
  autoRenew?: boolean;
  metadata?: Record<string, unknown>;
}

interface SubscribeResponse {
  status: number;
  message?: string;
  error?: string | string[];
  subscription?: UserSubscription;
}

interface MeResponse {
  status: number;
  subscription: UserSubscription | null;
  /** True for first-time subscribers — the plan's free trial applies. */
  trialEligible?: boolean;
}

export interface MySubscriptionInfo {
  subscription: UserSubscription | null;
  trialEligible: boolean;
}

/** POST /api/subscriptions — server creates Stripe Customer + Subscription, then persists. */
export const subscribeToPlan = async (
  payload: SubscribePayload
): Promise<UserSubscription> => {
  const res = await api.post<SubscribeResponse>('/subscriptions', payload);
  if (res.data.status !== 1 || !res.data.subscription) {
    const err = res.data.error;
    const msg = Array.isArray(err) ? err.join(', ') : err || 'Subscription failed';
    throw new Error(msg);
  }
  return res.data.subscription;
};

/** GET /api/subscriptions/me — latest subscription for the authenticated user. */
export const getMySubscription = async (): Promise<MySubscriptionInfo> => {
  const res = await api.get<MeResponse>('/subscriptions/me');
  const subscription = res.data.subscription ?? null;
  return {
    subscription,
    trialEligible: res.data.trialEligible ?? subscription === null,
  };
};

/** PATCH /api/subscriptions/:id/cancel — cancel at period end. */
export const cancelSubscription = async (
  id: number
): Promise<UserSubscription> => {
  const res = await api.patch<SubscribeResponse>(`/subscriptions/${id}/cancel`);
  if (!res.data.subscription) throw new Error('Cancel failed');
  return res.data.subscription;
};

/** PATCH /api/subscriptions/:id/resume — undo a cancellation before it ends. */
export const resumeSubscription = async (
  id: number
): Promise<UserSubscription> => {
  const res = await api.patch<SubscribeResponse>(`/subscriptions/${id}/resume`);
  if (!res.data.subscription) throw new Error('Resume failed');
  return res.data.subscription;
};
